<template>
  <div>
    <v-card class="pa-5">
      <v-card-text>
        <v-card-title>
          <h4>{{ $t('reports.operatorsWithDamage')}}</h4>
          <v-spacer></v-spacer>
          
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn @click="filterData()" v-on="on" v-bind="attrs" depressed class="float-right mr-2" color="primary">
                <v-icon small>fas fa-search</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('general.search') }}</span>
          </v-tooltip>
          
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn @click="resetFilter" v-on="on" v-bind="attrs" depressed class="float-right mr-2" color="primary">
                <v-icon small>fa-solid fa-eraser</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('general.resetFilter') }}</span>
          </v-tooltip>
          
          <v-btn v-if="reportOperators.length > 0" @click="download" :loading="loadingButton" color="primary">
            {{ $t('reports.downloadReport') }}
            <v-icon small class="ml-2">fas fa-cloud-download-alt</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form v-model="valid" lazy-validation ref="form">
            <v-row dense>
              <v-col cols="12" sm="6" md="4">
                <v-autocomplete
                  clearable
                  outlined
                  dense
                  :items="sites"
                  item-text="name"
                  item-value="id"
                  v-model="filter.site_id"
                  :label="$t('general.yard')"
                />
              </v-col>
              
              <v-col cols="12" sm="6" md="4">
                <v-autocomplete
                  clearable
                  outlined
                  dense
                  :items="users"
                  :item-text="getOperatorFullName"
                  item-value="id"
                  v-model="filter.operator_id"
                  :label="$t('general.operat')"
                >
                </v-autocomplete>
              </v-col>

              <v-col v-if="filter.dateRange" cols="12" sm="4" md="4">
                <v-menu
                  v-model="menu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filter.date_start"
                      :label="$t('general.startDate')"
                      outlined
                      dense
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      class="ml-2"
                    />
                  </template>
                  <v-date-picker
                    no-title
                    color="primary"
                    v-model="filter.date_start"
                    @input="handleDateStartSelection"
                    :allowed-dates="allowedStartDates"
                    :day-style="getDayStyle"
                  />
                </v-menu>
              </v-col>

              <v-col v-if="filter.dateRange" cols="12" sm="4" md="4">
                <v-menu
                  v-model="menu2"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filter.date_end"
                      :label="$t('general.endDate')"
                      outlined
                      dense
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      class="ml-2"
                    />
                  </template>
                  <v-date-picker
                    no-title
                    color="primary"
                    v-model="filter.date_end"
                    @input="menu2 = false"
                    :allowed-dates="allowedEndDates"
                    :day-style="getDayStyle"
                  />
                </v-menu>
              </v-col>

              <v-col cols="3">
                <v-checkbox v-model="filter.dateRange" :label="$t('general.dateRange')" hide-details class="mt-0"  color="primary"/>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-overlay absolute opacity="0" :value="loadingTable"/>
        
        <v-data-table
          :loading="loadingTable"
          :loading-text="$t('general.loading')"
          :headers="headers"
          :items="reportOperators"
          :expanded.sync="expanded"
          item-key="id"
          show-expand
          :single-expand="false"
          :server-items-length="optionsDataTable.totalItems"
          :options.sync="optionsDataTable"
          :footer-props="{ 'items-per-page-options': [15, 50, 70, 100] }"
          @update:page="get"
          @update:items-per-page="get"
          @update:sort-by="get"
          @update:sort-desc="get"
        >
          <template v-slot:item.name="{ item }">
            {{ item.name }} {{ item.last_name }}
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-4 grey lighten-5">
              <v-card flat>
                <v-card-title class="subtitle-2 py-2">
                  {{ $t('reports.inspectionDetails')}} - {{ item.name }} {{ item.last_name }}
                </v-card-title>
                <v-data-table
                  :headers="innerHeaders"
                  :items="item.inspections"
                  hide-default-footer
                  disable-pagination
                  class="elevation-1"
                  dense
                >
                 <template v-slot:item.unit="{ item }">
                    <span v-if="item.unit">
                      {{ item.unit.unit_number ? item.unit.unit_number : (item.unit.plates && item.unit.plates.length >
                        0 ? item.unit.plates[0] : 'S/N') }}
                    </span>
                    <span v-else>S/N</span>
                  </template>
                 </v-data-table>
              </v-card>
            </td>
          </template>
        </v-data-table>

      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      valid: true,
      loadingTable: false,
      loadingButton: false,
      menu: false,
      menu2: false,
      expanded: [], 
      optionsDataTable: {
        itemsPerPage: 15,
        page: 1,
        totalItems: 0,
        total: 0,
        sortBy: ['hard'],
        sortDesc: [true]
      },
      filter: {
        site_id: null,
        operator_id: null,
        dateRange: true,
        date_start: this.returnStartDate(),
        date_end: this.returnEndDate()
      },
      reportOperators: [],
      sites: [],
      users: []
    }
  },
  watch: {
    'filter.dateRange'(val) {
      if (!val) {
        this.filter.date_start = null
        this.filter.date_end = null
      } else {
        this.filter.date_start = this.returnStartDate()
        this.filter.date_end = this.returnEndDate()
      }
    }
  },
  computed: {
    headers() {
      return [
        { text: this.$t('general.operat'), align: 'start', value: 'name', sortable: true },
        { text: this.$t('general.totalDamages'), align: 'center', value: 'total', sortable: false },
        { text: this.$t('general.minorDamage'), align: 'center', value: 'soft', sortable: false },
        { text: this.$t('general.moderateDamage'), align: 'center', value: 'moderate', sortable: false },
        { text: this.$t('general.highDamage'), align: 'center', value: 'hard', sortable: false },
        { text: '', value: 'data-table-expand' } 
      ]
    },
    innerHeaders() {
      return [
        { text: this.$t('general.noInspection'), align: 'center', value: 'consecutive_number', sortable: false },
        { text: this.$t('general.date'), align: 'center', value: 'created_at', sortable: false },
        { text: 'CheckList', align: 'center', value: 'questionnaire', sortable: false },
        { text: this.$t('general.unit'), align: 'center', value: 'unit', sortable: false },
        { text: this.$t('general.inspector'), align: 'center', value: 'inspector', sortable: false },
        { text: this.$t('general.operat'), align: 'center', value: 'operator', sortable: false },
        { text: this.$t('general.yard'), align: 'center', value: 'site', sortable: false }
      ]
    }
  },
  created() {
    this.$store.dispatch('sites/get', { event: { context: this, item: { perPage: -1, page: 1 } } })
    this.$store.dispatch('users/getOperators', { event: { context: this, perPage: -1, page: 1 } })
    this.get()
  },
  methods: {
    returnStartDate() {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().substr(0, 10)
    },
    returnEndDate() {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().substr(0, 10)
    },
    getOperatorFullName(user) {
      return `${user.name} ${user.last_name}`;
    },
    resetFilter() {
      this.filter = {
        site_id: null,
        operator_id: null,
        dateRange: true,
        date_start: this.returnStartDate(),
        date_end: this.returnEndDate()
      }
      if (this.optionsDataTable.page === 1) return this.get()
      this.optionsDataTable.page = 1
    },
    filterData() {
      if (!this.$refs.form.validate()) return this.showNotification(this.$t('general.errorFilterMessage'), 'warn', -1)
      if (this.optionsDataTable.page === 1) return this.get()
      this.optionsDataTable.page = 1
    },
    download() {
      if (!this.$refs.form.validate()) return this.showNotification(this.$t('general.errorFilterMessage'), 'warn', -1)
      this.loadingButton = true
      
      this.$store.dispatch('reports/getOperatorCountDamagedDoc', {
        event: {
          context: this,
          params: {
            ord: this.optionsDataTable.sortDesc && this.optionsDataTable.sortDesc[0] ? 'DESC' : 'ASC',
            perPage: this.optionsDataTable.itemsPerPage,
            page: this.optionsDataTable.page,
            type: 'download',
            date_start: this.filter.date_start === null ? undefined : this.filter.date_start,
            date_end: this.filter.date_end === null ? undefined : this.filter.date_end,
            site_id: this.filter.site_id,
            operator_id: this.filter.operator_id,
            sortKey: this.optionsDataTable.sortBy.length > 0 ? this.optionsDataTable.sortBy[0] : 'hard'
          }
        }
      })
    },
    get() {
      this.loadingTable = true
      this.expanded = [] 
      
      this.$store.dispatch('reports/getOperatorCountDamaged', {
        event: {
          context: this,
          params: {
            date_start: this.filter.date_start === null ? undefined : this.filter.date_start,
            date_end: this.filter.date_end === null ? undefined : this.filter.date_end,
            site_id: this.filter.site_id,
            operator_id: this.filter.operator_id,
            perPage: this.optionsDataTable.itemsPerPage,
            page: this.optionsDataTable.page,
            type: 'json',
            sortKey: (this.optionsDataTable.sortBy && this.optionsDataTable.sortBy.length > 0) ? this.optionsDataTable.sortBy[0] : 'hard',
            ord: this.optionsDataTable.sortDesc && this.optionsDataTable.sortDesc[0] ? 'DESC' : 'ASC'
          }
        }
      })
    },
    handleDateStartSelection(date) {
      this.filter.date_start = date;
      this.updateDateEnd();
      this.menu = false;
    },
    allowedStartDates() { return true; },
    allowedEndDates(date) {
      if (!this.filter.date_start) return true;
      const startDate = new Date(this.filter.date_start);
      const endDateLimit = new Date(startDate);
      endDateLimit.setDate(startDate.getDate() + 29);
      return new Date(date) >= startDate && new Date(date) <= endDateLimit;
    },
    updateDateEnd() {
      if (this.filter.date_start) {
        const startDate = new Date(this.filter.date_start);
        const endDateLimit = new Date(startDate);
        endDateLimit.setDate(startDate.getDate() + 29);
        this.filter.date_end = endDateLimit.toISOString().substr(0, 10);
      }
    },
    getDayStyle(date) {
      if ((this.filter.date_start && this.allowedEndDates(date)) || (!this.filter.date_start && this.allowedStartDates(date))) {
        return null;
      }
      return { backgroundColor: 'rgba(0, 0, 0, 0.1)', color: 'gray' };
    }
  }
}
</script>